import { Dropdown as PrimeDropdown } from 'primereact/dropdown';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from 'react';

import Button from '../../Button';
import InputStyle from '../style.ts';
import { defaultSortFunc } from '../utils.ts';

import Style from './style';

const STAGE_PREFIX = '$STAGED_';
const CHIP_LIMIT = 3;

const withStageTag = (str) => `${STAGE_PREFIX}${str}`;

export default function InputMultiDropdown({
  containerStyle = {},
  inputList,
  isEnabled = true,
  isHidden = false,
  labelText,
  name,
  onChange,
  options: optionsProp = [],
  sortFunc = defaultSortFunc,
  useAutoAdd = true,
  values: valuesProp,
}) {
  const values = valuesProp || [];

  const [stagedValue, setStagedValue] = useState('');
  const [showAll, setShowAll] = useState(false);

  const addedValues = values.filter((v) => v.indexOf(STAGE_PREFIX) !== 0);

  const visibleValues = showAll
    ? addedValues
    : addedValues.slice(0, CHIP_LIMIT);

  const hiddenCount = addedValues.length - visibleValues.length;

  const options = useMemo(() => {
    const opts = inputList
      ? Object.values(inputList)
          .filter(Boolean)
          .map(({ label, value }) => ({ label, value }))
      : optionsProp.map((el) =>
          typeof el === 'object' ? el : { label: el, value: el },
        );

    return opts.sort(sortFunc);
  }, [inputList, optionsProp, sortFunc]);

  const labelMap = useMemo(() => {
    const map = new Map();
    options.forEach((o) => map.set(o.value, o.label));
    return map;
  }, [options]);

  useEffect(() => {
    if (stagedValue) {
      if (useAutoAdd) {
        addValue();
      } else {
        onChange({
          [name]: [...addedValues, withStageTag(stagedValue)],
        });
      }
    }
  }, [stagedValue]);

  useEffect(() => {
    if (!isEnabled || isHidden) {
      onChange({ [name]: [] });
    }
  }, [isEnabled, isHidden]);

  if (isHidden) return null;

  function reset() {
    setStagedValue('');
  }

  function addValue() {
    if (!stagedValue) return;

    if (values.includes(stagedValue)) {
      alert('Value already added');
      return reset();
    }

    const stagedIndex = values.indexOf(withStageTag(stagedValue));

    if (stagedIndex > -1) {
      const updated = [...values];
      updated[stagedIndex] = stagedValue;
      onChange({ [name]: updated });
      return reset();
    }

    onChange({ [name]: [...values, stagedValue] });
    reset();
  }

  function removeValue(val) {
    const updated = values.filter((v) => v !== val);
    onChange({ [name]: updated });
  }

  return (
    <span style={{ ...InputStyle.container, ...containerStyle }}>
      <label htmlFor={name} style={InputStyle.label(isEnabled)}>
        {labelText}
      </label>

      <div>
        <div style={Style.inputRow}>
          <PrimeDropdown
            id={name}
            value={stagedValue}
            options={options}
            optionLabel="label"
            optionValue="value"
            disabled={!isEnabled}
            style={Style.input}
            onChange={(e) => setStagedValue(e.value)}
          />

          <Button
            isEnabled={isEnabled}
            isHidden={useAutoAdd}
            text="Add"
            onClick={addValue}
          />
        </div>

        {values.length > 0 && (
          <div style={Style.chipsSection}>
            {visibleValues.map((val, i) => (
              <span key={val + i} style={Style.chip}>
                <span style={Style.chipText}>{labelMap.get(val) || val}</span>

                <Button
                  text="×"
                  isEnabled={isEnabled}
                  containerStyle={Style.chipCloseContainer}
                  style={Style.chipClose}
                  onClick={() => removeValue(val)}
                  additionalButtonProps={{
                    onMouseEnter: (e) => {
                      Object.assign(e.target.style, Style.chipCloseHover);
                    },
                    onMouseLeave: (e) => {
                      Object.assign(e.target.style, Style.chipClose);
                    },
                  }}
                />
              </span>
            ))}

            {!showAll && hiddenCount > 0 && (
              <button
                type="button"
                style={Style.moreChip}
                onClick={() => setShowAll(true)}
              >
                +{hiddenCount} more
              </button>
            )}

            {showAll && addedValues.length > CHIP_LIMIT && (
              <button
                type="button"
                style={Style.moreChip}
                onClick={() => setShowAll(false)}
              >
                show less
              </button>
            )}
          </div>
        )}
      </div>
    </span>
  );
}

InputMultiDropdown.propTypes = {
  containerStyle: PropTypes.object,
  inputList: PropTypes.object,
  isEnabled: PropTypes.bool,
  isHidden: PropTypes.bool,
  labelText: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  options: PropTypes.array,
  sortFunc: PropTypes.func,
  useAutoAdd: PropTypes.bool,
  values: PropTypes.array,
};
